// Horizontal photo strip for the catalog item-detail page.
//
// Layout:
//   - Row of 96x96 thumbnails (iOS photo-tile size), horizontal scroll on
//     overflow. Each thumbnail is tappable: short-tap opens a full-screen
//     zoom overlay; the ⨯ corner badge opens the delete-confirmation sheet.
//   - "Add photo" tile at the end: dashed border, camera glyph. Opens the
//     iPad rear camera via a hidden <input type="file" capture="environment">.
//   - Optimistic insert during upload — the new thumbnail appears with a
//     spinner overlay, and the item swaps to the real signed URL on success
//     or disappears on failure (with a toast).
//
// Rule 1: devices never write tables. uploadPhoto() goes through the API.
// Owner-approved amendments (docs/briefs/P1-03-catalog-photos.md):
//   - no pre-upload PII confirmation (dropped)
//   - delete-confirmation dialog required
//   - no role check in UI (any signed-in staff can upload / delete)

import { useRef, useState } from "react";
import { readSessionToken } from "../data/audit.ts";
import { deletePhoto, type Photo, uploadPhoto } from "../data/catalog.ts";

type Props = {
  itemId: string;
  photos: Photo[];
  onPhotosChange: (next: Photo[]) => void;
};

export function PhotoStrip({ itemId, photos, onPhotosChange }: Props) {
  // Local-state copy drives the UI; parent syncs on every real change.
  const [uploadingCount, setUploadingCount] = useState(0);
  const [zoomPhoto, setZoomPhoto] = useState<Photo | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Photo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const atCap = photos.length + uploadingCount >= 10;

  const onPickFile = () => {
    if (atCap) {
      setError("Up to 10 photos per item.");
      return;
    }
    fileInputRef.current?.click();
  };

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input immediately so picking the same file twice re-fires.
    e.target.value = "";
    if (!file) return;
    const token = readSessionToken();
    if (!token) {
      setError("Not signed in.");
      return;
    }
    setUploadingCount((n) => n + 1);
    setError(null);
    const result = await uploadPhoto(token, itemId, file);
    setUploadingCount((n) => Math.max(0, n - 1));
    if (!result.ok) {
      setError(errorMessage(result.error));
      return;
    }
    onPhotosChange([...photos, result.photo]);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const token = readSessionToken();
    if (!token) {
      setError("Not signed in.");
      setPendingDelete(null);
      return;
    }
    const photoId = pendingDelete.id;
    setPendingDelete(null);
    const result = await deletePhoto(token, photoId);
    if (!result.ok) {
      setError(errorMessage(result.error));
      return;
    }
    onPhotosChange(photos.filter((p) => p.id !== photoId));
  };

  return (
    <section className="space-y-2">
      <h3 className="px-4 text-[15px] uppercase tracking-[0.06em] font-semibold text-slate-500">
        Photos
      </h3>
      <div className="-mx-5 md:-mx-8 overflow-x-auto no-scrollbar">
        <ul className="flex gap-3 px-5 md:px-8 py-1 items-start">
          {photos.map((p) => (
            <li key={p.id} className="shrink-0">
              <Thumbnail
                photo={p}
                onOpen={() => setZoomPhoto(p)}
                onDelete={() => setPendingDelete(p)}
              />
            </li>
          ))}
          {/* Uploading placeholders — one per in-flight upload. Keys are
              intentionally index-based because the slots are indistinguishable
              and short-lived (lifetime of a single upload). biome-ignore. */}
          {Array.from({ length: uploadingCount }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: ephemeral upload slots, no stable id yet
            <li key={`uploading-${i}`} className="shrink-0">
              <UploadingTile />
            </li>
          ))}
          {!atCap && (
            <li className="shrink-0">
              <AddTile onClick={onPickFile} />
            </li>
          )}
        </ul>
      </div>

      {/* Hidden camera input — opens the iPad's rear camera on tap. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic"
        capture="environment"
        className="hidden"
        onChange={onFileChosen}
        tabIndex={-1}
      />

      {error && (
        <p className="px-4 text-[15px] text-[#ff3b30]" role="alert">
          {error}
        </p>
      )}

      {zoomPhoto && <ZoomOverlay photo={zoomPhoto} onClose={() => setZoomPhoto(null)} />}

      {pendingDelete && (
        <DeleteSheet onCancel={() => setPendingDelete(null)} onConfirm={confirmDelete} />
      )}
    </section>
  );
}

/* ─────────────────── tiles ─────────────────────────────────────────────── */

function Thumbnail({
  photo,
  onOpen,
  onDelete,
}: {
  photo: Photo;
  onOpen: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="relative h-24 w-24">
      <button
        type="button"
        onClick={onOpen}
        className="h-full w-full rounded-xl overflow-hidden bg-slate-100 border border-slate-200/70 active:opacity-70 transition"
        aria-label="Open photo"
      >
        {photo.signedUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={photo.signedUrl}
            alt={photo.caption ?? "Product photo"}
            className="h-full w-full object-contain"
            loading="lazy"
          />
        ) : (
          <BrokenGlyph />
        )}
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-slate-900/80 text-white grid place-items-center shadow-md backdrop-blur-sm active:bg-slate-900 transition"
        aria-label="Delete photo"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <title>Delete</title>
          <path d="M3 3L9 9M9 3L3 9" />
        </svg>
      </button>
    </div>
  );
}

function UploadingTile() {
  return (
    <div className="h-24 w-24 rounded-xl bg-slate-100 border border-slate-200/70 grid place-items-center">
      <Spinner />
    </div>
  );
}

function AddTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="h-24 w-24 rounded-xl border-2 border-dashed border-slate-300 bg-white grid place-items-center text-slate-400 active:bg-slate-50 transition"
      aria-label="Add photo"
    >
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
        <path
          d="M4 9a2 2 0 012-2h2.5l1.4-2h8.2l1.4 2H22a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V9z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle cx="14" cy="14" r="4" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    </button>
  );
}

function BrokenGlyph() {
  return (
    <div className="h-full w-full grid place-items-center text-slate-400">
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M3 5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5z"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path d="M3 15l5-5 4 4 3-3 6 6" stroke="currentColor" strokeWidth="1.5" />
        <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    </div>
  );
}

function Spinner() {
  return (
    <svg
      className="animate-spin h-6 w-6 text-slate-400"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path
        d="M22 12a10 10 0 01-10 10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/* ─────────────────── zoom + delete overlays ────────────────────────────── */

function ZoomOverlay({ photo, onClose }: { photo: Photo; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-label="Photo zoom"
      className="fixed inset-0 z-50 bg-black/90 grid place-items-center p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      {photo.signedUrl && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={photo.signedUrl}
          alt={photo.caption ?? "Product photo"}
          className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
        />
      )}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 text-white grid place-items-center backdrop-blur-md"
        aria-label="Close"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <title>Close</title>
          <path d="M4 4L14 14M14 4L4 14" />
        </svg>
      </button>
    </div>
  );
}

function DeleteSheet({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Delete photo confirmation"
      className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-end sm:place-items-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="px-5 py-6 text-center">
          <h3 className="text-[17px] font-semibold text-slate-900">Delete photo?</h3>
          <p className="mt-1 text-[15px] text-slate-500">This can't be undone from here.</p>
        </div>
        <div className="border-t border-slate-200/80">
          <button
            type="button"
            onClick={onConfirm}
            className="w-full h-[52px] text-[17px] font-semibold text-[#ff3b30] active:bg-slate-100 transition"
          >
            Delete
          </button>
        </div>
        <div className="border-t border-slate-200/80">
          <button
            type="button"
            onClick={onCancel}
            className="w-full h-[52px] text-[17px] text-[#007aff] active:bg-slate-100 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── helpers ───────────────────────────────────────────── */

function errorMessage(code: string): string {
  switch (code) {
    case "photo_too_large":
      return "Photo too large — must be under 10 MB.";
    case "mime_not_allowed":
      return "File type not allowed — use a photo (JPEG, PNG, WebP, HEIC).";
    case "photos_cap_exceeded":
      return "Up to 10 photos per item.";
    case "network_error":
      return "No network. Try again.";
    case "storage_put_failed":
      return "Upload failed. Try again.";
    case "storage_not_configured":
      return "Photo storage not configured yet.";
    case "storage_unavailable":
      return "Photo storage temporarily unavailable.";
    default:
      return "Something went wrong. Try again.";
  }
}
